import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Drop, Printer, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, photoHref, formatApiErrorDetail } from "@/lib/api";
import { LABEL_PRINT_CSS } from "@/components/LabelDialog";

const sinkName = (s) => [s.brand, s.model].filter(Boolean).join(" ") || "Sink";
const sinkImg = (s) => {
  const p = (s.photos || []).find((x) => x && x.storage_path) || (s.photos || [])[0];
  return p?.storage_path ? photoHref(p.storage_path) : "";
};

const SinkLabel = ({ a, customerName, projectName, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="max-h-[92vh] w-auto max-w-none overflow-y-auto border-zinc-800 bg-zinc-950 p-4"
      data-testid="label-dialog"
    >
      <style>{LABEL_PRINT_CSS}</style>
      <DialogTitle className="sr-only">Sink reserve label 4×6</DialogTitle>
      <div className="no-print mb-3 flex justify-end">
        <Button size="sm" onClick={() => window.print()} data-testid="sink-label-print-btn">
          <Printer size={15} weight="bold" className="mr-1" /> Print 4×6
        </Button>
      </div>
      {a && (
        <div
          id="print-area"
          className="label-sheet print-label relative mx-auto flex flex-col bg-white p-[0.25in] text-black shadow-2xl"
          style={{ width: "4in", height: "6in" }}
          data-testid="sink-label-sheet"
        >
          <div className="mb-2 flex items-end justify-between border-b-2 border-black pb-1">
            <div className="text-[13pt] font-black uppercase tracking-widest">Reserved Sink</div>
            <div className="font-mono text-[10pt] font-black">{a.code}</div>
          </div>
          {(customerName || projectName) && (
            <div className="mb-2 grid grid-cols-2 gap-2" data-testid="sink-label-customer">
              {customerName && (
                <div>
                  <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-500">Customer</div>
                  <div className="text-[12pt] font-bold leading-tight">{customerName}</div>
                </div>
              )}
              {projectName && (
                <div>
                  <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-500">Project</div>
                  <div className="text-[12pt] font-bold leading-tight">{projectName}</div>
                </div>
              )}
            </div>
          )}
          {a.supplier && (
            <div className="mb-2 -mx-[0.25in] bg-black px-[0.25in] py-1.5 text-white" data-testid="sink-label-vendor">
              <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-400">Vendor</div>
              <div className="text-[15pt] font-black leading-tight">{a.supplier}</div>
            </div>
          )}
          <div className="mb-2" data-testid="sink-label-model">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-500">Sink</div>
                <div className="text-[15pt] font-bold leading-tight">{[a.brand, a.model].filter(Boolean).join(" ")}</div>
              </div>
              {a.photo && (
                <img
                  src={photoHref(a.photo)}
                  alt=""
                  className="h-[1.2in] w-[1.2in] shrink-0 rounded border-2 border-black object-cover"
                  data-testid="sink-label-photo"
                />
              )}
            </div>
          </div>
          {a.description && (
            <div className="mb-2" data-testid="sink-label-description">
              <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-500">Description</div>
              <div className="text-[11pt] font-semibold leading-snug">{a.description.slice(0, 160)}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {a.sink_type && (
              <div data-testid="sink-label-type">
                <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-500">Type</div>
                <div className="text-[11pt] font-bold">{a.sink_type}</div>
              </div>
            )}
            {a.size && (
              <div data-testid="sink-label-size">
                <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-500">Size</div>
                <div className="text-[11pt] font-bold">{a.size}</div>
              </div>
            )}
          </div>
          {a.location && (
            <div className="mt-2 rounded border-2 border-black px-2 py-1" data-testid="sink-label-shelf">
              <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-500">Shelf / location</div>
              <div className="text-[12pt] font-black">{a.location}</div>
            </div>
          )}
          <div className="mt-auto text-[8pt] uppercase tracking-widest text-neutral-500">Qty {a.qty}</div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const SinkAssign = ({ entity, entityId, customerName = "", projectName = "" }) => {
  const [stock, setStock] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState("");
  const [labelFor, setLabelFor] = useState(null);

  const load = useCallback(async () => {
    const [all, mine] = await Promise.all([
      api.get("/sinks"),
      api.get("/sink-allocations", { params: { entity, entity_id: entityId } }),
    ]);
    setStock(all.data);
    setAssigned(mine.data.items);
  }, [entity, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const assign = async (sinkId) => {
    setBusy(true);
    try {
      await api.post(`/sinks/${sinkId}/assign`, { entity, entity_id: entityId, qty: 1 });
      toast.success("Sink reserved from stock");
      setPickerOpen(false);
      setQ("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reserve sink");
    }
    setBusy(false);
  };

  const release = async (a) => {
    await api.delete(`/sinks/${a.sink_id}/assign/${a.allocation_id}`);
    toast.success("Sink released back to stock");
    load();
  };

  const available = useMemo(() => {
    const avail = stock;
    const needle = q.trim().toLowerCase();
    if (!needle) return avail;
    return avail.filter((s) =>
      [s.brand, s.model, s.supplier, s.description, s.sink_type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [stock, q]);

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid="sink-assign-panel">
      <div className="flex items-center gap-2">
        <Drop size={16} weight="bold" className="text-indigo-300" />
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Sinks from stock ({assigned.length})
        </div>
      </div>

      <div className="mt-3">
        <Button
          data-testid="open-sink-picker"
          onClick={() => setPickerOpen(true)}
          className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400 sm:w-auto sm:px-6"
        >
          <Drop size={15} weight="bold" className="mr-2" /> Pick a sink in stock
        </Button>
      </div>

      <div className="mt-3 space-y-2">
        {assigned.length === 0 && (
          <p className="text-sm text-zinc-500">No sinks reserved from stock yet.</p>
        )}
        {assigned.map((a) => (
          <div
            key={a.allocation_id}
            data-testid={`assigned-sink-${a.allocation_id}`}
            className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
          >
            <span className="min-w-0 flex-1">
              <span className="font-mono-data block text-xs text-yellow-500">{a.code}</span>
              <span className="flex items-center gap-1.5">
                <span className="truncate text-sm font-semibold">
                  {[a.brand, a.model].filter(Boolean).join(" ")} × {a.qty}
                </span>
                {a.backorder && (
                  <span
                    data-testid={`assigned-sink-backorder-${a.allocation_id}`}
                    title="Reserved while out of stock — needs ordering"
                    className="shrink-0 rounded-full border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-300"
                  >
                    Backorder
                  </span>
                )}
              </span>
              <span className="block truncate text-xs text-zinc-500">
                {[
                  a.supplier && `by ${a.supplier}`,
                  a.description,
                  a.sink_type,
                  a.size,
                  a.location && `shelf ${a.location}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <button
              data-testid={`print-sink-label-${a.allocation_id}`}
              onClick={() => setLabelFor(a)}
              className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-indigo-300 hover:text-indigo-200"
            >
              <Printer size={13} weight="bold" /> Label
            </button>
            <button
              data-testid={`release-sink-${a.allocation_id}`}
              onClick={() => release(a)}
              className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-red-400"
            >
              Release
            </button>
          </div>
        ))}
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent
          className="max-h-[88vh] overflow-hidden border-zinc-800 bg-zinc-900 sm:max-w-3xl"
          data-testid="sink-picker-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Pick a sink in stock</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Click a sink to reserve it from stock.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="sink-picker-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search brand, model, vendor…"
              className="h-10 border-zinc-800 bg-zinc-950 pl-9 text-sm"
            />
          </div>
          <div className="mt-3 grid max-h-[60vh] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
            {available.length === 0 && (
              <div className="col-span-full py-8 text-center text-sm text-zinc-500">No sinks match.</div>
            )}
            {available.map((s) => {
              const img = sinkImg(s);
              return (
                <button
                  key={s.id}
                  data-testid={`sink-card-${s.id}`}
                  disabled={busy}
                  onClick={() => assign(s.id)}
                  className="group flex flex-col overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 text-left transition-colors duration-150 hover:border-indigo-500 disabled:opacity-50"
                >
                  <div className="flex h-24 items-center justify-center overflow-hidden bg-zinc-900">
                    {img ? (
                      <img src={img} alt={sinkName(s)} className="h-full w-full object-cover" />
                    ) : (
                      <Drop size={26} weight="duotone" className="text-zinc-700" />
                    )}
                  </div>
                  <div className="flex-1 p-2">
                    <div className="truncate text-xs font-bold text-zinc-100">{sinkName(s)}</div>
                    {s.supplier && (
                      <div className="truncate text-[10px] uppercase tracking-wide text-indigo-300/80">{s.supplier}</div>
                    )}
                    {s.description && (
                      <div className="mt-0.5 truncate text-[11px] text-zinc-500">{s.description}</div>
                    )}
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      {s.stocked === false ? "Supplier stock" : `${s.quantity} on hand`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <SinkLabel
        a={labelFor}
        customerName={customerName}
        projectName={projectName}
        open={!!labelFor}
        onOpenChange={(v) => !v && setLabelFor(null)}
      />
    </div>
  );
};
